import { ActiveFiltersSummaryList } from "@/components/data-table/components/ActiveFiltersSummary/ActiveFiltersSummaryList";
import {
  QUERY_TABLE_FS_BADGE_TRIGGER_LAYOUT_CLASS,
  activeFiltersBarToneClasses,
  type ActiveFiltersBarStripTone,
} from "@/components/data-table/components/DataTableActiveFiltersBar/activeFiltersBarShared";
import type { ActiveFilterDescriptor } from "@/components/data-table/utils/activeFilterDescriptors";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { QueryTableFilteredSortedBadgeDivider } from "./QueryTableFilteredSortedBadgeDivider";
import { QueryTableFilteredSortedBadgeFilterSegment } from "./QueryTableFilteredSortedBadgeFilterSegment";
import { QueryTableFilteredSortedBadgeSortSegment } from "./QueryTableFilteredSortedBadgeSortSegment";

export interface QueryTableFilteredSortedBadgeProps {
  tone: ActiveFiltersBarStripTone;
  showFilterSegment: boolean;
  showSortSegment: boolean;
  sortDirection: "asc" | "desc" | null;
  committedFilterCount: number;
  filterCombinedLabel: string;
  filterPendingHint: string;
  sortDetailLine: string | null;
  descriptors: ActiveFilterDescriptor[];
  onRemoveColumnFilter: (columnId: string) => void;
  onClearFilters: () => void;
  onClearSorting: () => void;
  getColumnVisibilityToggle?: (
    columnId: string,
  ) => { isVisible: boolean; onToggle: () => void } | undefined;
  className?: string;
}

const QueryTableFilteredSortedBadgeComp = ({
  tone,
  showFilterSegment,
  showSortSegment,
  sortDirection,
  committedFilterCount,
  filterCombinedLabel,
  filterPendingHint,
  sortDetailLine,
  descriptors,
  onRemoveColumnFilter,
  onClearFilters,
  onClearSorting,
  getColumnVisibilityToggle,
  className,
}: QueryTableFilteredSortedBadgeProps) => {
  const { t } = useTranslation();
  const badgeChrome = activeFiltersBarToneClasses(tone);
  const isMobileViewport = useIsMobile();
  const [open, setOpen] = useState(false);

  const handleClearFilters = useCallback(() => {
    onClearFilters();
    setOpen(false);
  }, [onClearFilters]);

  const handleClearSorting = useCallback(() => {
    onClearSorting();
    setOpen(false);
  }, [onClearSorting]);

  const stripTitle = useMemo(() => {
    const chunks: string[] = [];
    if (showFilterSegment) {
      chunks.push(filterCombinedLabel.trim() || filterPendingHint);
    }
    if (showSortSegment && sortDetailLine) {
      chunks.push(sortDetailLine);
    }
    return chunks.join("\u2003·\u2003");
  }, [
    filterCombinedLabel,
    filterPendingHint,
    showFilterSegment,
    showSortSegment,
    sortDetailLine,
  ]);

  const filtersSectionBody = (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-left text-sm font-semibold leading-tight text-foreground">
          {t("table.footer.badgeFiltersHeading")}
        </h3>
        {descriptors.length > 0 ? (
          <p className="mt-1.5 text-left text-xs leading-snug text-muted-foreground">
            {filterCombinedLabel}
          </p>
        ) : (
          <p className="mt-1.5 text-left text-sm leading-snug text-muted-foreground">
            {filterPendingHint}
          </p>
        )}
      </div>
      {descriptors.length > 0 ? (
        <ActiveFiltersSummaryList
          descriptors={descriptors}
          onRemoveColumnFilter={onRemoveColumnFilter}
          onClearAll={handleClearFilters}
          getColumnVisibilityToggle={getColumnVisibilityToggle}
        />
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-full text-xs"
          onClick={handleClearFilters}
        >
          {t("table.clearFilters")}
        </Button>
      )}
    </div>
  );

  const sortSectionBody =
    showSortSegment && sortDetailLine ? (
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="text-left text-sm font-semibold leading-tight text-foreground">
            {t("table.footer.badgeSortHeading")}
          </h3>
          <p className="mt-1.5 text-left text-xs leading-snug text-muted-foreground">
            {sortDetailLine}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-full text-xs"
          onClick={handleClearSorting}
        >
          {t("table.clearSorting")}
        </Button>
      </div>
    ) : null;

  const detailPanelDesktop = (
    <div className="flex max-h-[min(85dvh,520px)] min-w-[min(100vw-2rem,20rem)] max-w-[22rem] flex-col gap-4 overflow-y-auto p-3 overscroll-contain">
      {showFilterSegment ? filtersSectionBody : null}
      {showFilterSegment && showSortSegment ? <Separator /> : null}
      {sortSectionBody}
    </div>
  );

  const detailPanelMobileScroll = (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
      {showFilterSegment ? filtersSectionBody : null}
      {showFilterSegment && showSortSegment ? <Separator /> : null}
      {sortSectionBody}
    </div>
  );

  const triggerButton = (
    <Button
      variant="outline"
      size="sm"
      title={stripTitle}
      className={cn(
        QUERY_TABLE_FS_BADGE_TRIGGER_LAYOUT_CLASS,
        badgeChrome.footerBadgeSurfaceClass,
        badgeChrome.footerBadgeHoverClass,
        badgeChrome.footerBadgeFocusRingOffsetClass,
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        {showFilterSegment ? (
          <QueryTableFilteredSortedBadgeFilterSegment
            committedFilterCount={committedFilterCount}
            segmentMutedClassName={badgeChrome.footerBadgeSegmentMutedClass}
          />
        ) : null}
        {showFilterSegment && showSortSegment ? (
          <QueryTableFilteredSortedBadgeDivider
            dividerClassName={badgeChrome.footerBadgeVerticalDividerClass}
          />
        ) : null}
        {showSortSegment ? (
          <QueryTableFilteredSortedBadgeSortSegment
            sortDirection={sortDirection}
            segmentMutedClassName={badgeChrome.footerBadgeSegmentMutedClass}
          />
        ) : null}
      </span>
    </Button>
  );

  if (isMobileViewport) {
    return (
      <span
        className={cn(
          "ml-auto flex shrink-0 items-center justify-end",
          className,
        )}
      >
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>{triggerButton}</SheetTrigger>
          <SheetContent
            side="bottom"
            className={cn(
              "flex max-h-[min(85dvh,560px)] flex-col gap-0 overflow-hidden rounded-t-xl border-0 p-0",
              "[&>button.absolute]:right-3 [&>button.absolute]:top-3.5",
            )}
          >
            <div className="shrink-0 border-b px-4 pb-3 pt-4 pr-12">
              <SheetTitle className="text-left text-base font-semibold leading-tight">
                {t("table.footer.badgeSheetTitle")}
              </SheetTitle>
            </div>
            {detailPanelMobileScroll}
          </SheetContent>
        </Sheet>
      </span>
    );
  }

  return (
    <span
      className={cn(
        "ml-auto flex shrink-0 items-center justify-end",
        className,
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="end">
          {detailPanelDesktop}
        </PopoverContent>
      </Popover>
    </span>
  );
};

export const QueryTableFilteredSortedBadge = memo(
  QueryTableFilteredSortedBadgeComp,
);
